import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { setMeta } from "../lib/seo";

export default function BlogPage() {
  const [q, setQ] = useState("");
  const [activeTag, setActiveTag] = useState("");

  useEffect(() => {
    setMeta(
      "Blog — Buying Guides, Comparisons & Deals | ShoppinMagic",
      "Honest buying guides, product comparisons, and seasonal shopping guides from the ShoppinMagic team."
    );
  }, []);

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["blog", q, activeTag],
    queryFn: () => api.getBlogPosts(q ? { q } : activeTag ? { tag: activeTag } : {}),
  });

  const { data: allPosts = [] } = useQuery({ queryKey: ["blog", "all"], queryFn: () => api.getBlogPosts() });
  const tags = [...new Set(allPosts.flatMap((p) => p.tags || []))].slice(0, 8);
  const [featured, ...rest] = posts;

  return (
    <div data-testid="blog-page" className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 text-center">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">The ShoppinMagic Blog</h1>
        <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500 sm:text-base">
          Buying guides, honest comparisons, and deal roundups — researched so you don't have to.
        </p>
        <div className="relative mx-auto mt-5 max-w-md">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles…"
            data-testid="blog-search-input"
            className="w-full rounded-full border border-slate-300 bg-white py-3 pl-4 pr-12 text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-200"
          />
          <Search size={17} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
        </div>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => setActiveTag("")}
            data-testid="blog-tag-all"
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${!activeTag ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-400"}`}
          >
            All topics
          </button>
          {tags.map((t) => (
            <button
              key={t}
              onClick={() => setActiveTag(t)}
              data-testid={`blog-tag-${t}`}
              className={`rounded-full px-4 py-1.5 text-xs font-bold capitalize transition-colors ${activeTag === t ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-400"}`}
            >
              {t.replace(/-/g, " ")}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-xl bg-slate-100" />)}
        </div>
      ) : posts.length === 0 ? (
        <p className="py-16 text-center text-slate-500" data-testid="blog-no-results">No articles found. Try another search.</p>
      ) : (
        <>
          {featured && (
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-featured-${featured.slug}`}
              className="group mb-8 grid overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md md:grid-cols-2"
            >
              <div className="aspect-[16/9] overflow-hidden md:aspect-auto">
                <img src={featured.image} alt={featured.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              </div>
              <div className="flex flex-col justify-center p-6 sm:p-8">
                <span className="text-xs font-bold uppercase tracking-wider text-gold-600">Featured · {featured.category}</span>
                <h2 className="font-heading mt-2 text-xl font-extrabold text-slate-900 group-hover:text-gold-600 sm:text-2xl">{featured.title}</h2>
                <p className="mt-2 text-sm text-slate-500">{featured.excerpt}</p>
                <p className="mt-4 text-xs text-slate-400">{featured.author} · {featured.date} · {featured.read_time} min read</p>
              </div>
            </Link>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={post.image} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-gold-600">{post.category}</span>
                  <h3 className="mt-1 line-clamp-2 text-base font-bold text-slate-900 group-hover:text-gold-600">{post.title}</h3>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-500">{post.excerpt}</p>
                  <p className="mt-2 text-xs text-slate-400">{post.author} · {post.read_time} min read</p>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
